import { useMemo, useState } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { STATUS_OPTIONS } from '@cms/utils';
import toast from 'react-hot-toast';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import type { DesktopPluginItem } from '@cms/models';
import {
  desktopPluginsQueryKeys,
  useDesktopPluginDownloadUrl,
  useDesktopPlugins,
  useDeleteDesktopPlugin,
  usePublishDesktopPlugin,
} from '@/lib/api/desktop-plugins.api';
import { PluginFormModal, type PluginFormModalMode } from './PluginFormModal';
import { Button } from '@/components/ui/button';
import { AdminTableRowActions } from '@/components/common/AdminTableRowActions';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';

type PluginStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let n = bytes;
  while (n >= 1024 && idx < units.length - 1) {
    n /= 1024;
    idx++;
  }
  const rounded = idx === 0 ? n : Math.round(n * 10) / 10;
  return `${rounded} ${units[idx]}`;
}

export function PluginsPage() {
  const { col, act, page, statusOptions, allStatusesOption } = useAdminI18n();
  const queryClient = useQueryClient();
  const query = useDesktopPlugins({ includeDraft: true });
  const downloadUrlMutation = useDesktopPluginDownloadUrl();
  const publishMutation = usePublishDesktopPlugin();
  const deleteMutation = useDeleteDesktopPlugin();

  const [sorting, setSorting] = useState<SortingState>([{ id: 'pluginKey', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PluginStatusFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: PluginFormModalMode;
    plugin: DesktopPluginItem | null;
  }>({ open: false, mode: 'create', plugin: null });

  const handleCopy = async (row: DesktopPluginItem) => {
    try {
      if (!row.id) {
        toast.error('Missing plugin id');
        return;
      }
      const out = await downloadUrlMutation.mutateAsync({ pluginId: row.id });
      if (!out?.downloadUrl) throw new Error('Missing downloadUrl');
      await globalThis.navigator.clipboard.writeText(out.downloadUrl);
      const msg = out.expiresInSec > 0 
        ? `Copied link (expires in ${out.expiresInSec}s)` 
        : `Copied link`;
      toast.success(msg);
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handlePublish = async (pluginId: string | undefined | null) => {
    try {
      if (!pluginId) {
        toast.error('Missing plugin id');
        return;
      }
      await publishMutation.mutateAsync(pluginId);
      toast.success('Plugin published');
      await queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleDelete = async (pluginId: string | undefined | null) => {
    try {
      if (!pluginId) {
        toast.error('Missing plugin id');
        return;
      }
      if (!globalThis.confirm('Delete this plugin record?')) return;
      await deleteMutation.mutateAsync(pluginId);
      toast.success('Plugin deleted');
      await queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const plugins = query.data ?? [];
  const filteredRows = useMemo(() => {
    let list = plugins;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(item => {
        const haystack = [
          item.appSlug,
          item.pluginKey,
          item.platform,
          item.arch ?? '',
          item.fileName,
          item.description ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(item =>
        statusFilter === 'active' ? item.publishedAt != null : item.publishedAt == null,
      );
    }
    return list;
  }, [plugins, search, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sorting.length) return filteredRows;
    const { id, desc } = sorting[0]!;
    return [...filteredRows].sort((a, b) => {
      const av = String((a as Record<string, unknown>)[id] ?? '');
      const bv = String((b as Record<string, unknown>)[id] ?? '');
      const cmp = av.localeCompare(bv);
      return desc ? -cmp : cmp;
    });
  }, [filteredRows, sorting]);

  const totalRecords = sortedRows.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);
  const pageState: PaginationState = { pageIndex: safePageIndex, pageSize };
  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePageIndex, pageSize]);

  const columns: DynamicTableColumnConfig<DesktopPluginItem>[] = useMemo(
    () => [
      {
        id: 'no',
        header: col('no'),
        columnType: 'number',
        cell: ({ index }) => pageState.pageIndex * pageState.pageSize + index + 1,
        widthMode: 'fit',
        size: 64,
        meta: { className: 'w-16 text-muted-foreground' },
      },
      {
        id: 'appSlug',
        accessorKey: 'appSlug',
        header: col('app'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 110,
      },
      {
        id: 'pluginKey',
        accessorKey: 'pluginKey',
        header: col('plugin'),
        columnType: 'text',
        sortable: true,
        widthMode: 'fit',
        size: 100,
      },
      {
        id: 'platform',
        accessorKey: 'platform',
        header: col('platform'),
        columnType: 'text',
        sortable: false,
        widthMode: 'fit',
        size: 90,
      },
      {
        id: 'arch',
        accessorKey: 'arch',
        header: col('arch'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => {
          if (value == null || value === '') return 'any';
          return String(value);
        },
        widthMode: 'fit',
        size: 70,
      },
      {
        id: 'fileName',
        accessorKey: 'fileName',
        header: col('file'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => {
          const fileName = typeof value === 'string' ? value : '';
          return (
            <div className="max-w-[11rem] truncate" title={fileName}>
              {fileName || '—'}
            </div>
          );
        },
        widthMode: 'fit',
        size: 170,
      },
      {
        id: 'sizeBytes',
        accessorKey: 'sizeBytes',
        header: col('size'),
        columnType: 'text',
        sortable: false,
        cell: ({ value }) => formatBytes(typeof value === 'number' ? value : Number(value)),
        widthMode: 'fit',
        size: 86,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: col('status'),
        columnType: 'badge',
        sortable: false,
        displayLabelOptions: statusOptions,
        widthMode: 'fit',
        size: 90,
      },
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: col('updated'),
        columnType: 'text',
        sortable: true,
        cell: ({ value }) => {
          if (!value) return '—';
          try {
            return new Date(value as string).toLocaleString();
          } catch {
            return String(value);
          }
        },
        widthMode: 'fit',
        size: 145,
      },
      {
        id: 'actions',
        header: col('actions'),
        columnType: 'custom',
        sortable: false,
        cell: ({ row }) => (
          <AdminTableRowActions
            act={act}
            compact
            onView={() => setFormModal({ open: true, mode: 'view', plugin: row })}
            onEdit={() => setFormModal({ open: true, mode: 'edit', plugin: row })}
            onDelete={() => void handleDelete(row.id)}
            isDeleting={deleteMutation.isPending}
            extra={
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={downloadUrlMutation.isPending || row.publishedAt == null}
                  onClick={() => void handleCopy(row)}
                >
                  Copy link
                </Button>
                {row.publishedAt == null && (
                  <Button
                    type="button"
                    size="sm"
                    className={cn('h-7 px-2 text-xs', publishMutation.isPending && 'opacity-70')}
                    disabled={publishMutation.isPending}
                    onClick={() => void handlePublish(row.id)}
                  >
                    Publish
                  </Button>
                )}
              </>
            }
          />
        ),
        widthMode: 'fit',
        size: 340,
        minSize: 280,
        meta: { className: 'text-right' },
      },
    ],
    [
      act,
      col,
      deleteMutation.isPending,
      downloadUrlMutation.isPending,
      pagination.pageIndex,
      pagination.pageSize,
      publishMutation.isPending,
      statusOptions,
    ],
  );

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div>
        <h1 className="text-3xl font-bold">{page('desktop_plugins', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('desktop_plugins', 'subtitle')}</p>
      </div>

      <div className="flex items-center justify-end gap-3 flex-wrap">
        <Button
          type="button"
          onClick={() => setFormModal({ open: true, mode: 'create', plugin: null })}
          className="shadow-sm"
        >
          Upload plugin
        </Button>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="desktop-plugins-search"
            label="Search"
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="Search by plugin, platform, file..."
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="desktop-plugins-status"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as PluginStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <DynamicTable<DesktopPluginItem>
          features={{
            columns: true,
            rows: true,
            sorting: true,
            pagination: true,
            stripedRows: true,
            fontSize: 'xs',
            compactPadding: true,
          }}
          columns={columns}
          rows={{ data: paginatedRows, getRowId: row => String(row.id ?? '') }}
          controllers={controllers}
          isLoading={query.isLoading}
          loadingMessage={page('desktop_plugins', 'loading')}
          emptyMessage={page('desktop_plugins', 'empty')}
        />
      </div>

      <PluginFormModal
        open={formModal.open}
        onClose={() => setFormModal(prev => ({ ...prev, open: false, plugin: null }))}
        mode={formModal.mode}
        plugin={formModal.plugin}
        onSuccess={() => {
          toast.success('Plugins updated');
          void queryClient.invalidateQueries({ queryKey: desktopPluginsQueryKeys.all });
          setFormModal(prev => ({ ...prev, open: false, plugin: null }));
        }}
      />
    </div>
  );
}
